import React, { useMemo, useEffect, useState, useCallback, forwardRef } from "react";
import { CalloutProps, CalloutStatusType } from "./types";
import cx from "components/src/utils/cx";
import { useIdRef } from "shared/src/hooks/useIdRef";
import { Heading, Body } from "components/src/components/Typography/Typography";
import { Icon } from "components/src/components/Icon/Icon";
import { Button } from "components/src/components/Button/Button";
import { ButtonProps, ButtonStatusType } from "../Button/types";
import { Loading } from "../Loading/Loading";
import { Badge } from "../Badge/Badge";
import { CalloutArrow } from "./components/CalloutArrow/CalloutArrow";

const variantOptions = ["default", "minimal"];

const statusClasses = {
  default: {
    default: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-bg-layer-two",
      fg: "cui-text-fg-moderate",
      titleColor: "cui-text-fg-moderate",
      borderColor: "cui-border-fg-moderate",
      borderSize: "cui-border-1",
    },
    minimal: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-bg-default",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-fg-moderate",
      borderColor: "cui-border-bg-default",
      borderSize: "cui-border-1",
    },
  },
  alert: {
    default: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-status-alert-minimal",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-status-alert-strong",
      borderColor: "cui-border-status-alert",
      borderSize: "cui-border-1",
    },
    minimal: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-bg-default",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-status-alert-strong",
      borderColor: "cui-border-bg-default",
      borderSize: "cui-border-1",
    },
  },
  positive: {
    default: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-status-positive-minimal",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-status-positive-strong",
      borderColor: "cui-border-status-positive",
      borderSize: "cui-border-1",
    },
    minimal: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-bg-default",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-status-positive-strong",
      borderColor: "cui-border-bg-default",
      borderSize: "cui-border-1",
    },
  },
  negative: {
    default: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-status-negative-minimal",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-status-negative-strong",
      borderColor: "cui-border-status-negative",
      borderSize: "cui-border-1",
    },
    minimal: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-bg-default",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-status-negative-strong",
      borderColor: "cui-border-bg-default",
      borderSize: "cui-border-1",
    },
  },
  info: {
    default: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-status-informational-minimal",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-status-informational-strong",
      borderColor: "cui-border-status-informational",
      borderSize: "cui-border-1",
    },
    minimal: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-bg-default",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-status-informational-strong",
      borderColor: "cui-border-bg-default",
      borderSize: "cui-border-1",
    },
  },
  tooltip: {
    default: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-status-informational",
      fg: "cui-text-status-informational-minimal",
      titleColor: "cui-text-status-informational-minimal",
      borderColor: "cui-border-status-informational",
      borderSize: "cui-border-1",
      badgeBgColor: "status-informational-minimal",
      badgeTextColor: "status-informational-strong",
    },
    minimal: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-status-informational",
      fg: "cui-text-status-informational-minimal",
      titleColor: "cui-text-status-informational-minimal",
      borderColor: "cui-border-status-informational",
      borderSize: "cui-border-1",
    },
  },
  pending: {
    default: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-bg-layer-one",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-fg-moderate",
      borderColor: "cui-border-fg-moderate",
      borderSize: "cui-border-1",
    },
    minimal: {
      shared: "cui-border-solid",
      rounded: "cui-rounded-2",
      bg: "cui-bg-bg-default",
      fg: "cui-text-fg-default",
      titleColor: "cui-text-fg-moderate",
      borderColor: "cui-border-bg-default",
      borderSize: "cui-border-1",
    },
  },
};

const statusImages = (size) => {
  return {
    default: null,
    alert: <Icon name="ui_warning" color="status-alert-strong" size={size} />,
    positive: (
      <Icon
        name="ui_checkmark_circle"
        color="status-positive-strong"
        size={size}
      />
    ),
    negative: (
      <Icon name="ui_error" color="status-negative-strong" size={size} />
    ),
    info: (
      <Icon name="ui_info" color="status-informational-strong" size={size} />
    ),
    pending: <Loading variant="subtle" size="sm" />,
  };
};

const statusToButtonStatusMap: Record<string, ButtonStatusType> = {
  default: null,
  positive: "completed",
  negative: "destructive",
  alert: "alert",
  info: null,
  pending: null,
};

const tertiaryStatuses = ["negative", "info", "pending", "default", "tooltip"];

export const Callout = forwardRef<HTMLDivElement | null, CalloutProps>(({
  id,
  title,
  description,
  icon = "",
  iconSize = "md",
  variant = "default",
  status = null,
  className = "",
  inline = false,
  showTitle = true,
  showDescription = true,
  showIcon = true,
  showPrimaryCta = true,
  showSecondaryCta = true,
  showDismiss = true,
  maxWidth = Infinity,
  minWidth = 100,
  closeButton,
  primaryCta,
  secondaryCta,
  visible = true,
  animation = true,
  keepAlive = false,
  mode = "light",
  badge,
  image,
  withArrow,
  arrowPosition,
  position = "relative",
  indentDescription = false,
  onClose,
  onPrimaryCtaClick,
  onSecondaryCtaClick,
  style,
  ...rest
}: CalloutProps, ref) => {
  const [show, setShow] = useState(visible);
  const _status = status ?? "default";

  const classes = useMemo(() => {
    const currentStatusClasses = Object.values(
      statusClasses[_status][variant]
    ).join(" ");
    return cx(
      {
        [currentStatusClasses]: true,
        "cui-opacity-100": visible,
        "cui-opacity-0": !visible,
        "cui-transition-opacity cui-duration-200": animation,
        "cui-flex-col": !inline,
        "cui-items-center": inline,
        "cui-relative": position === "relative",
        "cui-absolute": position === "absolute"
      },
      `cui__callout cui-flex cui-justify-between cui-p-sm cui-w-auto ${className}`.trim()
    );
  }, [className, status, variant, visible, animation]);
  const _id = useIdRef({ str: "cui__callout-", id }).current;

  const _styles = useMemo(() => {
    return {
      maxWidth: maxWidth !== Infinity ? maxWidth : "",
      minWidth,
      ...style,
    };
  }, [style, maxWidth, minWidth]);

  const currentIcon = useMemo(() => {
    if (!showIcon) return null;
    const _statusImages = statusImages(iconSize);
    if (_status !== "default" && _status in _statusImages)
      return _statusImages[_status];
    if (typeof icon === "string")
      return (
        <Icon
          name={icon}
          size={iconSize}
          color={statusClasses[_status][variant].titleColor}
        />
      );
    if (typeof icon === "object")
      return (
        <Icon
          size={iconSize}
          color={statusClasses[_status][variant].titleColor}
          {...icon}
        />
      );
  }, [icon, _status, variant, iconSize]);

  const _variant = useMemo(
    () => (variantOptions.includes(variant) ? variant : "default"),
    [variant]
  );

  /**
   * Handlers
   */
  const handleAnimationEnd = (e) => {
    visible ? setShow(true) : setShow(false);
  };

  const handleClose = useCallback(
    (e) => {
      typeof onClose === "function" && onClose({ e, id: _id });
    },
    [onClose]
  );

  const handlePrimaryCtaClick = useCallback(
    (e) => {
      typeof onPrimaryCtaClick === "function" &&
        onPrimaryCtaClick({ e, id: _id });
    },
    [onPrimaryCtaClick]
  );
  const handleSecondaryCtaClick = useCallback(
    (e) => {
      typeof onSecondaryCtaClick === "function" &&
        onSecondaryCtaClick({ e, id: _id });
    },
    [onSecondaryCtaClick]
  );

  /**
   * Button Configs (props) - use defaults, but overrideable via props
   */
  const closeButtonProps = useMemo<{ onClick: (e: any) => void, status: CalloutStatusType | "default"
  }>(() => {
    return {
      status: _status,
      onClick: handleClose,
    };
  }, [onClose, _status]);

  const primaryCtaProps = useMemo<ButtonProps>(() => {
    const isTertiary =
      tertiaryStatuses.includes(_status) || _variant === "minimal";
    return {
      text: "Accept",
      size: "xs",
      type: isTertiary
        ? "tertiary"
        : _status === "alert"
          ? "primary"
          : "secondary",
      status: statusToButtonStatusMap[_status],
      leftIcon: null,
      "data-identifier": `callout-primary-cta`,
      onClick: handlePrimaryCtaClick,
      ...primaryCta,
    };
  }, [primaryCta, handlePrimaryCtaClick, status, inline]);

  const secondaryCtaProps = useMemo<ButtonProps>(() => {
    const isAlertStatus = _status === "alert" && _variant === "default";
    return {
      text: "Accept",
      size: "xs",
      type:
        isAlertStatus ? "tertiary" : "minimal",
      leftIcon: null,
      status: statusToButtonStatusMap[_status],
      "data-identifier": `callout-secondary-cta`,
      forceReadability:
        isAlertStatus
          ? undefined
          : _status === "tooltip" ? { type: mode === "light" ? "onDark" : "onLight" } : { type: mode === "light" ? "onLight" : "onDark" },
      onClick: handleSecondaryCtaClick,
      ...secondaryCta,
    };
  }, [secondaryCta, handleSecondaryCtaClick, status, mode]);

  useEffect(() => {
    const t = setTimeout(() => {
      setShow(visible);
    }, 205);
    return () => {
      clearTimeout(t);
    };
  }, [visible]);

  if (!show && !keepAlive) return null;
  const hasIndentedDescription = indentDescription && (showTitle && (!!icon || !!badge) && !image );
  return (
    <div
      id={_id}
      className={classes}
      style={_styles}
      onTransitionEnd={handleAnimationEnd}
      {...rest}
      ref={ref}
    >
      { withArrow ? <CalloutArrow position={arrowPosition} /> : null }
      <CalloutHeader
        icon={currentIcon}
        title={title}
        showTitle={showTitle}
        inline={inline}
        status={_status}
        variant={_variant}
        badge={badge}
        closeButtonProps={closeButtonProps}
        showDismiss={showDismiss}
      />

      <div className="cui-flex-col cui-justify-between cui-items-start cui-gap-xs">
        {!showTitle && showIcon ? currentIcon : null}
        {image ? (
          <img
            src={image}
            className="cui-my-2xs cui-max-w-full cui-h-auto cui-object-contain"
          />
        ) : null}
        {showDescription ? (
          <Body
            className={`cui-m-0 ${ !inline ? "cui-mt-2xs" : ""} ${hasIndentedDescription ? "cui-ml-lg" : ""}`}
            color={statusClasses[_status][variant].fg}
            size="sm"
          >
            {description}
          </Body>
        ) : (
          <div className="cui-flex-grow" />
        )}
        {inline ? (
          <CalloutCTA
            primaryCtaProps={primaryCtaProps}
            secondaryCtaProps={secondaryCtaProps}
            closeButtonProps={closeButtonProps}
            showPrimaryCta={showPrimaryCta}
            showSecondaryCta={showSecondaryCta}
            showDismiss={showDismiss}
            inline={inline}
          />
        ) : null}
      </div>
      {!inline ? (
        <div>
          <CalloutCTA
            primaryCtaProps={primaryCtaProps}
            secondaryCtaProps={secondaryCtaProps}
            closeButtonProps={closeButtonProps}
            showPrimaryCta={showPrimaryCta}
            showSecondaryCta={showSecondaryCta}
            showDismiss={showDismiss}
            inline={inline}
          />
        </div>
      ) : null}
    </div>
  );
});

const CalloutHeader = ({
  icon,
  title,
  showTitle,
  inline,
  status,
  variant,
  badge,
  closeButtonProps,
  showDismiss,
}) => {
  
  const _badgeDefaults = {
    type: "strong",
    size: "xs",
    shape: "square",
    fgColor: statusClasses[status][variant]?.badgeTextColor ?? "fg-inverse",
    bgColor: statusClasses[status][variant]?.badgeBgColor ?? "fg-moderate",
  };
  const _badgeProps = useMemo(()=>{
    return badge && typeof badge === "string"
      ? {
          text: badge,
          ..._badgeDefaults,
        }
      : typeof badge === "object"
        ? { ..._badgeDefaults, ...badge }
        : {};
  }, [badge]);

  return (
    <div
      className={`cui-flex cui-gap-xs cui-justify-between ${inline ? "cui-items-center" : "cui-items-start"} cui-mb-2xs`}
    >
      {badge ? <Badge {..._badgeProps} /> : null}
      {showTitle ? (
        <>
          {icon}
          <Heading
            className={`${!icon ? "cui-ml-xs" : ""} cui-flex-grow`}
            size="md"
            color={statusClasses[status][variant].titleColor}
          >
            {title}
          </Heading>
        </>
      ) : (
        <div className="cui-flex-grow" />
      )}
      {!inline && showDismiss ? (
        <div className="-cui-mt-1">
          <CalloutCloseButton {...closeButtonProps} />
        </div>
      ) : null}
    </div>
  );
};

const CalloutCTA = ({
  inline,
  primaryCtaProps,
  secondaryCtaProps,
  closeButtonProps,
  showSecondaryCta,
  showPrimaryCta,
  showDismiss,
}) => {
  return (
    <div className="cui-flex cui-justify-end cui-gap-xs cui-mt-xs">
      {showSecondaryCta ? <Button {...secondaryCtaProps} /> : null}
      {showPrimaryCta ? <Button {...primaryCtaProps} /> : null}
      {inline && showDismiss ? (
        <CalloutCloseButton {...closeButtonProps} />
      ) : null}
    </div>
  );
};

const CalloutCloseButton = ({ onClick, status }) => {
  return (
    <button
      className="cui-h-6 cui-w-6 cui-flex cui-items-center cui-justify-center cui-rounded-[9999px] cui-border-none cui-bg-neutrals-transparent hover:cui-bg-neutrals-opacity-20 active:cui-bg-neutrals-opacity-30 cui-cursor-pointer"
      onClick={onClick}
    >
      <Icon name="ui_x" color={`${status === "tooltip" ? "fg-inverse" : "fg-moderate" }`} size="xs" />
    </button>
  );
};
